<script setup lang="ts">
// Calendar heatmap — one cell per day across `spec.range`. Range
// accepts the rolling-window DateRange syntax; for `calendar`
// specifically, `{ end: "today", days: N }` is clamped to 1830
// (5 years × 366) at validation time.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { CalendarChartSpec, DateRange } from "../sbi/protocol";
import { CHART_COLORS } from "../theme.ts";
import {
  axisLabelDefaults,
  chartChrome,
  resolveCssVar,
  splitLineDefaults,
  tooltipDefaults,
  tooltipTextStyle,
} from "./echarts-base";
import { calendarLayout } from "./calendar-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: CalendarChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function resolveRange(range: DateRange): [string, string] {
  if (Array.isArray(range)) {
    return [range[0]!, range[1]!];
  }
  const today = new Date();
  if ("days" in range) {
    const start = new Date(today);
    start.setUTCDate(start.getUTCDate() - (range as { days: number }).days);
    return [isoDate(start), isoDate(today)];
  }
  // Named window
  const w = (range as { window: string }).window;
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  const startOfMonth = (yy: number, mm: number) =>
    new Date(Date.UTC(yy, mm, 1));
  const endOfMonth = (yy: number, mm: number) =>
    new Date(Date.UTC(yy, mm + 1, 0));
  switch (w) {
    case "last_7d": {
      const s = new Date(today);
      s.setUTCDate(s.getUTCDate() - 7);
      return [isoDate(s), isoDate(today)];
    }
    case "last_30d": {
      const s = new Date(today);
      s.setUTCDate(s.getUTCDate() - 30);
      return [isoDate(s), isoDate(today)];
    }
    case "last_90d": {
      const s = new Date(today);
      s.setUTCDate(s.getUTCDate() - 90);
      return [isoDate(s), isoDate(today)];
    }
    case "last_12m": {
      const s = new Date(Date.UTC(y - 1, m, d));
      return [isoDate(s), isoDate(today)];
    }
    case "ytd":
      return [`${y}-01-01`, isoDate(today)];
    case "this_month":
      return [isoDate(startOfMonth(y, m)), isoDate(today)];
    case "last_month":
      return [
        isoDate(startOfMonth(y, m - 1)),
        isoDate(endOfMonth(y, m - 1)),
      ];
    case "this_quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return [isoDate(startOfMonth(y, qStart)), isoDate(today)];
    }
    case "last_quarter": {
      const qStart = Math.floor(m / 3) * 3 - 3;
      const yy = qStart < 0 ? y - 1 : y;
      const mm = ((qStart % 12) + 12) % 12;
      return [isoDate(startOfMonth(yy, mm)), isoDate(endOfMonth(yy, mm + 2))];
    }
    case "this_year":
      return [`${y}-01-01`, isoDate(today)];
    case "last_year":
      return [`${y - 1}-01-01`, `${y - 1}-12-31`];
  }
  // Unreachable — DateRange.window is a closed enum, but TS can't narrow
  // the parameterised string back to the discriminated literal here.
  return [`${y}-01-01`, isoDate(today)];
}

const range = computed<[string, string]>(() => resolveRange(props.spec.range));

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const points = computed(() =>
  rows.value
    .map((r) => [String(r[props.spec.date] ?? ""), Number(r[props.spec.value])])
    .filter(
      (p): p is [string, number] =>
        typeof p[0] === "string" && Number.isFinite(p[1] as number),
    ),
);

const valueRange = computed<[number, number]>(() => {
  let lo = Infinity;
  let hi = -Infinity;
  for (const [, v] of points.value) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo)) return [0, 1];
  return [lo, hi];
});

// Orientation, cell size and label forms from the chart width — a
// narrow screen gets a vertical calendar; print stays horizontal (see
// calendar-layout.ts).
const layout = computed(() =>
  calendarLayout({
    range: range.value,
    width: ctx.value.width,
    print: ctx.value.print,
    specHeight: props.spec.height,
  }),
);

const option = computed(() => ({
  ...chartChrome(ctx.value),
  tooltip: tooltipDefaults("item", ctx.value),
  visualMap: {
    min: valueRange.value[0],
    max: valueRange.value[1],
    show: true,
    orient: "horizontal" as const,
    left: "center" as const,
    bottom: 8,
    itemWidth: 12,
    itemHeight: layout.value.rampLength,
    // Mono mini-scale numbers so the visualMap legend matches the rest
    // of the chart text. Color falls back to the global `textStyle`
    // (chartChrome) — which is warm card-ink — so the scale reads
    // clearly without needing its own colour token.
    textStyle: { fontFamily: tooltipTextStyle().fontFamily, fontSize: 10 },
  },
  // Explicit positioning keeps the grid inside the container — without
  // these, ECharts' calendar defaults don't reserve room for the
  // visualMap legend at the bottom or the month/day labels at the top
  // and left, so cells spill past the chart's edges.
  calendar: {
    range: range.value,
    orient: layout.value.orient,
    cellSize: layout.value.cellSize,
    left: layout.value.left,
    right: layout.value.right,
    top: layout.value.top,
    bottom: layout.value.bottom,
    // Cell-border splitlines (canvas-painted): the previous
    // `var(--lucent-calendar-grid)` reference couldn't resolve in
    // canvas and painted black. Warm dashed gridline keeps the
    // calendar's cell grid visible but subordinate to the heatmap.
    splitLine: { ...splitLineDefaults() },
    // Background colour for cells with no data — embedder token first,
    // canvas-safe paper-tint hex fallback. Without resolution canvas
    // would paint with an invalid colour (effectively transparent),
    // which is functional but reads as "missing" rather than "empty".
    itemStyle: {
      color:
        resolveCssVar("var(--lucent-calendar-empty)") ??
        CHART_COLORS.calendarEmpty,
      borderColor: "transparent",
    },
    yearLabel: { show: false },
    // Day-of-week and month labels — previously pointed at
    // `var(--lucent-card-muted)` which canvas can't resolve. Mono
    // warm-grey to match the editorial register.
    dayLabel: { ...axisLabelDefaults(), ...layout.value.dayLabel },
    monthLabel: { ...axisLabelDefaults(), ...layout.value.monthLabel },
  },
  series: [
    {
      type: "heatmap" as const,
      coordinateSystem: "calendar" as const,
      data: points.value,
    },
  ],
}));

// Default height has to fit 7 day-rows + month/day labels + the
// visualMap legend. At 200px (the previous default) the grid was so
// squashed that cells rendered as paper-thin slivers and the legend
// frequently fell off the bottom. 220px keeps a year-view legible
// without dominating the page; authors can override per-spec.
// A vertical calendar sizes itself from its weeks (up to 900px).
const height = computed(
  () => layout.value.height ?? resolveChartHeight("calendar", props.spec.height),
);

function onClick(p: { data?: unknown }) {
  if (!props.spec.id || !p?.data) return;
  const point = p.data as [string, number] | undefined;
  if (!point) return;
  emit("click-target", props.spec.id, {
    date: point[0],
    value: point[1],
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
